import { ShowRowVenue, useShowRowContext } from "@/components/shows";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/ui/tooltip";

import Link from "next/link";
import { bodyHtmlToPlainTextClient } from "@punchup/utils/rich-text/plain-text-to-body-html";
import { useCallback, useState } from "react";

export function ShowRowTimestampMetadata() {
  const { shows, uniqueVenues } = useShowRowContext();
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useCallback((node: HTMLParagraphElement | null) => {
    if (!node) return;
    setIsTruncated(node.scrollHeight > node.clientHeight);
  }, []);

  const getTitleAndMetadata = (
    title: string | undefined,
    metadataText: string
  ) => {
    const nonEmptyMetadata = metadataText && metadataText.length > 0;

    // if there's a title, display it
    if (title) {
      // if we also have metadata, append it, separating using a dash
      if (nonEmptyMetadata) {
        return `${title} – ${metadataText}`;
      }
      return title;
    }

    // otherwise, if there's no title, display only the metadata text if it exists
    if (nonEmptyMetadata) {
      return metadataText;
    }

    return "";
  };

  const containerClassName =
    "flex flex-row items-center justify-between w-full";

  if (shows.length > 1) {
    // note: no Link for multiple locations yet, we don't have show/event pages for multiple events yet.
    return (
      <div className={containerClassName}>
        <div className="flex flex-col gap-2 md:gap-1.5 flex-1">
          <div className="flex flex-col gap-1">
            <p className="text-primary-blue text-sm md:text-base font-semibold">
              <span>{shows.length} shows</span>
            </p>
            <ShowRowVenue
              multipleVenues={shows.length > 1 && uniqueVenues.length > 1}
            />
          </div>
        </div>
      </div>
    );
  }

  const show = shows[0];
  // metadata_text may be rich HTML (PUN-5353); this row shows a plain-text
  // subtitle + tooltip, so flatten it client-side — no literal tags on the feed
  // / comedian page / search / nearby surfaces, and entities decode so an
  // editor-saved `&amp;` reads as `&`, never the raw entity.
  const metadataText = bodyHtmlToPlainTextClient(show.metadataText ?? "");
  const hasTitleOrMetadata = show.title || metadataText;

  // otherwise, there must be just 1 show, so display a Link
  return (
    <Link href={`/e/${show.id}`} className={containerClassName}>
      <div className="flex flex-col gap-2 md:gap-1.5 flex-1">
        <div className="flex flex-col gap-1">
          <p className="text-primary-blue text-sm md:text-base font-semibold">
            <span>{show.datetime.format("dddd")} – </span>
            <span>{show.datetime.format("h:mm A")}</span>
          </p>
          <ShowRowVenue
            multipleVenues={shows.length > 1 && uniqueVenues.length > 1}
          />
        </div>
        {/* if there's a title or metadata text for the show, display it with a tooltip */}
        {hasTitleOrMetadata && (
          <TooltipProvider>
            <Tooltip delayDuration={100} open={isTruncated ? undefined : false}>
              <TooltipTrigger asChild>
                <p
                  ref={textRef}
                  className="text-primary-blue text-sm line-clamp-2 md:line-clamp-1 font-normal"
                >
                  {getTitleAndMetadata(show.title, metadataText)}
                </p>
              </TooltipTrigger>
              <TooltipContent sideOffset={5} className="max-w-md text-sm">
                {getTitleAndMetadata(show.title, metadataText)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </Link>
  );
}
